import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoicesAPI, productsAPI, customersAPI, warehousesAPI, inventoryAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import BarcodeScanner from '../common/BarcodeScanner';
import toast from 'react-hot-toast';

const GST_RATES = [0, 5, 12, 18, 28];

export default function InvoiceForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const [invoiceType, setInvoiceType] = useState('proforma');
  const [customerId, setCustomerId] = useState('');
  const [warehouseId, setWarehouseId] = useState(user?.warehouse_id || '');
  const [supplyStateCode, setSupplyStateCode] = useState('');
  const [paymentMode, setPaymentMode] = useState('credit');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);

  useEffect(() => {
    customersAPI.list({ limit: 200 }).then(r => setCustomers(r.data.customers)).catch(() => {});
    warehousesAPI.list().then(r => {
      setWarehouses(r.data);
      if (!warehouseId && r.data.length > 0) {
        const def = r.data.find(w => w.is_default) || r.data[0];
        setWarehouseId(def.id);
      }
    }).catch(() => {});
  }, [warehouseId]);

  // Product search
  const searchProducts = useCallback(async (q) => {
    if (q.length < 2) { setProductResults([]); return; }
    try {
      const res = await productsAPI.list({ search: q, status: 'active', limit: 10 });
      setProductResults(res.data.products);
      setShowProductSearch(true);
    } catch { }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearch), 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Add product to invoice
  const addProduct = async (product) => {
    setShowProductSearch(false);
    setProductSearch('');
    
    // Check if already added
    if (items.find(i => i.product_id === product.id && !i.batch_id)) {
      toast.error('Product already added');
      return;
    }

    // Fetch batches for the product
    let batches = [];
    try {
      const res = await inventoryAPI.batches(product.id, { warehouse_id: warehouseId });
      batches = res.data;
    } catch { }

    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      generic_name: product.generic_name,
      hsn_code: product.hsn_code,
      drug_schedule: product.drug_schedule,
      unit_of_measure: product.unit_of_measure,
      pack_size: product.pack_size,
      batch_id: batches[0]?.id || '',
      batch_number: batches[0]?.batch_number || '',
      expiry_date: batches[0]?.expiry_date || '',
      available_qty: batches[0]?.quantity || 0,
      batches: batches,
      quantity: 1,
      free_quantity: 0,
      unit_price: Number(product.selling_price) || Number(product.mrp) || 0,
      mrp: Number(product.mrp) || 0,
      discount_pct: 0,
      gst_rate: Number(product.gst_rate) || 12,
      cess_rate: Number(product.cess_rate) || 0,
    }]);
  };

  // Handle barcode scan
  const handleBarcodeScan = async (barcode) => {
    setShowScanner(false);
    try {
      const res = await productsAPI.lookupBarcode(barcode);
      if (res.data) {
        toast.success(`Found: ${res.data.name}`);
        await addProduct(res.data);
      }
    } catch {
      // Barcode not found — try text search
      try {
        const searchRes = await productsAPI.list({ search: barcode, limit: 1 });
        if (searchRes.data.products.length > 0) {
          toast.success(`Found: ${searchRes.data.products[0].name}`);
          await addProduct(searchRes.data.products[0]);
        } else {
          toast.error(`No product found for barcode: ${barcode}`);
        }
      } catch { toast.error(`Product not found: ${barcode}`); }
    }
  };

  // Update item
  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      // If batch changed, update batch info
      if (field === 'batch_id') {
        const batch = item.batches.find(b => b.id === value);
        if (batch) {
          updated.batch_number = batch.batch_number;
          updated.expiry_date = batch.expiry_date;
          updated.available_qty = batch.quantity;
          updated.unit_price = Number(batch.selling_price) || updated.unit_price;
          updated.mrp = Number(batch.mrp);
        }
      }
      return updated;
    }));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // Calculate totals
  const calcItemTotal = (item) => {
    const gross = item.quantity * item.unit_price;
    const discount = (gross * item.discount_pct) / 100;
    const taxable = gross - discount;
    const customer = customers.find(c => c.id === customerId);
    const isIGST = customer?.state_code && customer.state_code !== (supplyStateCode || '27');
    const gstAmt = (taxable * item.gst_rate) / 100;
    return {
      gross, discount, taxable,
      cgst: isIGST ? 0 : gstAmt / 2,
      sgst: isIGST ? 0 : gstAmt / 2,
      igst: isIGST ? gstAmt : 0,
      total: taxable + gstAmt,
      isIGST
    };
  };

  const summary = items.reduce((acc, item) => {
    const calc = calcItemTotal(item);
    return {
      subtotal: acc.subtotal + calc.gross,
      discount: acc.discount + calc.discount,
      taxable: acc.taxable + calc.taxable,
      cgst: acc.cgst + calc.cgst,
      sgst: acc.sgst + calc.sgst,
      igst: acc.igst + calc.igst,
      total: acc.total + calc.total,
      isIGST: calc.isIGST
    };
  }, { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0, isIGST: false });

  const roundedTotal = Math.round(summary.total);
  const roundOff = (roundedTotal - summary.total).toFixed(2);

  const handleSubmit = async () => {
    if (!customerId) { toast.error('Please select a customer'); return; }
    if (!warehouseId) { toast.error('Please select a warehouse'); return; }
    if (items.length === 0) { toast.error('Please add at least one product'); return; }

    const customer = customers.find(c => c.id === customerId);
    setLoading(true);
    try {
      const payload = {
        invoice_type: invoiceType,
        customer_id: customerId,
        warehouse_id: warehouseId,
        supply_state_code: customer?.state_code || supplyStateCode || '27',
        place_of_supply: customer?.state || '',
        payment_mode: paymentMode,
        notes,
        items: items.map(item => ({
          product_id: item.product_id,
          batch_id: item.batch_id || null,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date || null,
          quantity: Number(item.quantity),
          free_quantity: Number(item.free_quantity) || 0,
          unit_price: Number(item.unit_price),
          mrp: Number(item.mrp),
          discount_pct: Number(item.discount_pct) || 0,
          gst_rate: Number(item.gst_rate),
          cess_rate: Number(item.cess_rate) || 0,
        }))
      };

      const res = await invoicesAPI.create(payload);
      toast.success(`${invoiceType === 'proforma' ? 'Proforma' : 'GST'} Invoice created: ${res.data.invoice_number}`);
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invoice');
    } finally { setLoading(false); }
  };

  const fmt = (n) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="page-header">
        <div><h1>Create Invoice</h1><p className="subtitle">Generate Proforma or GST Invoice</p></div>
        <button onClick={() => navigate('/invoices')} className="btn btn-secondary">Cancel</button>
      </div>

      {/* Invoice Type Toggle */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="flex gap-3 items-center" style={{ flexWrap: 'wrap' }}>
            <span className="font-semibold text-secondary" style={{ marginRight: 8 }}>Invoice Type:</span>
            <button className={`btn ${invoiceType === 'proforma' ? 'btn-accent' : 'btn-outline'}`}
              onClick={() => setInvoiceType('proforma')}>Proforma Invoice</button>
            <button className={`btn ${invoiceType === 'gst_invoice' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setInvoiceType('gst_invoice')}>GST Invoice</button>
            <div style={{ flex: 1 }} />
            {invoiceType === 'proforma' && (
              <span className="badge badge-orange">Payment not yet received — no stock deduction</span>
            )}
            {invoiceType === 'gst_invoice' && (
              <span className="badge badge-green">Final tax invoice — stock will be deducted</span>
            )}
          </div>
        </div>
      </div>

      {/* Customer & Warehouse Selection */}
      <div className="card mb-4">
        <div className="card-header"><h2>Customer & Details</h2></div>
        <div className="card-body">
          <div className="form-row form-row-3">
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <select className="form-select" value={customerId} onChange={e => {
                setCustomerId(e.target.value);
                const c = customers.find(c => c.id === e.target.value);
                if (c?.state_code) setSupplyStateCode(c.state_code);
              }}>
                <option value="">Select Customer</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name} {c.gstin ? `(${c.gstin})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Warehouse *</label>
              <select className="form-select" value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
                <option value="">Select Warehouse</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Payment Mode</label>
              <select className="form-select" value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
                <option value="credit">Credit</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="neft">NEFT/RTGS</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Product Items */}
      <div className="card mb-4">
        <div className="card-header">
          <h2>Invoice Items</h2>
          <span className="text-sm text-secondary">{items.length} item(s)</span>
        </div>
        <div className="card-body">
          {/* Product Search + Barcode Scanner */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input className="form-input" placeholder="Search products by name, SKU, barcode..."
                value={productSearch} onChange={e => setProductSearch(e.target.value)}
                onFocus={() => productResults.length > 0 && setShowProductSearch(true)} />
              {showProductSearch && productResults.length > 0 && (
              <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid var(--border)', borderRadius:8, boxShadow:'var(--shadow-md)', zIndex:20, maxHeight:300, overflowY:'auto' }}>
                {productResults.map(p => (
                  <div key={p.id} onClick={() => addProduct(p)}
                    style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--border-light)', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      <div className="text-xs text-secondary">{p.sku} • {p.generic_name} • HSN: {p.hsn_code} • GST: {p.gst_rate}%</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">₹{Number(p.selling_price || p.mrp).toFixed(2)}</div>
                      <div className="text-xs text-secondary">Stock: {p.total_stock || 0}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
            <button onClick={() => setShowScanner(true)} className="btn btn-accent" style={{ whiteSpace: 'nowrap', height: 40 }}>
              📷 Scan Barcode
            </button>
          </div>

          {/* Barcode Scanner Modal */}
          {showScanner && (
            <BarcodeScanner
              onScan={handleBarcodeScan}
              onClose={() => setShowScanner(false)}
              title="Scan Product Barcode"
              continuous={true}
            />
          )}

          {items.length > 0 ? (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={{width:30}}>#</th>
                    <th>Product</th>
                    <th>Batch</th>
                    <th style={{width:70}}>Qty</th>
                    <th style={{width:70}}>Free</th>
                    <th style={{width:100}}>Rate (₹)</th>
                    <th style={{width:70}}>Disc%</th>
                    <th style={{width:70}}>GST%</th>
                    <th style={{textAlign:'right'}}>Taxable</th>
                    <th style={{textAlign:'right'}}>GST Amt</th>
                    <th style={{textAlign:'right'}}>Total</th>
                    <th style={{width:40}}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const calc = calcItemTotal(item);
                    return (
                      <tr key={idx}>
                        <td className="text-secondary">{idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{item.product_name}</div>
                          <div className="text-xs text-secondary">{item.hsn_code} • {item.unit_of_measure} {item.pack_size ? `(${item.pack_size})` : ''}</div>
                        </td>
                        <td>
                          {item.batches.length > 0 ? (
                            <select className="form-select" style={{ fontSize: 12, padding: '4px 8px', minWidth: 140 }}
                              value={item.batch_id} onChange={e => updateItem(idx, 'batch_id', e.target.value)}>
                              {item.batches.map(b => (
                                <option key={b.id} value={b.id}>
                                  {b.batch_number} (Exp: {new Date(b.expiry_date).toLocaleDateString('en-IN',{month:'short',year:'2-digit'})}) [{b.quantity}]
                                </option>
                              ))}
                            </select>
                          ) : <span className="text-xs text-danger">No batches</span>}
                        </td>
                        <td><input type="number" min="1" className="form-input" style={{ padding:'4px 8px', width:65 }}
                          value={item.quantity} onChange={e => updateItem(idx, 'quantity', Number(e.target.value) || 1)} /></td>
                        <td><input type="number" min="0" className="form-input" style={{ padding:'4px 8px', width:55 }}
                          value={item.free_quantity} onChange={e => updateItem(idx, 'free_quantity', Number(e.target.value) || 0)} /></td>
                        <td><input type="number" step="0.01" className="form-input" style={{ padding:'4px 8px', width:95 }}
                          value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', Number(e.target.value) || 0)} /></td>
                        <td><input type="number" step="0.5" min="0" max="100" className="form-input" style={{ padding:'4px 8px', width:60 }}
                          value={item.discount_pct} onChange={e => updateItem(idx, 'discount_pct', Number(e.target.value) || 0)} /></td>
                        <td>
                          <select className="form-select" style={{ fontSize: 12, padding: '4px 6px', width:65 }}
                            value={item.gst_rate} onChange={e => updateItem(idx, 'gst_rate', Number(e.target.value))}>
                            {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </td>
                        <td className="td-amount">{fmt(calc.taxable)}</td>
                        <td className="td-amount">{fmt(calc.cgst + calc.sgst + calc.igst)}</td>
                        <td className="td-amount font-bold">{fmt(calc.total)}</td>
                        <td>
                          <button onClick={() => removeItem(idx)} className="btn btn-icon" style={{ color:'var(--danger)', background:'transparent' }} title="Remove">✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 32 }}>
              <div className="empty-icon">📦</div>
              <h3>No items added</h3>
              <p>Search and add products above</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      {items.length > 0 && (
        <div className="grid-2">
          <div className="card">
            <div className="card-header"><h2>Notes</h2></div>
            <div className="card-body">
              <textarea className="form-textarea" placeholder="Invoice notes / terms..." value={notes} onChange={e => setNotes(e.target.value)} rows={4} />
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h2>Invoice Summary</h2></div>
            <div className="card-body" style={{ padding: '12px 20px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:14 }}>
                <span className="text-secondary">Subtotal</span><span className="font-mono">₹{fmt(summary.subtotal)}</span>
              </div>
              {summary.discount > 0 && <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:14 }}>
                <span className="text-secondary">Discount</span><span className="font-mono text-danger">-₹{fmt(summary.discount)}</span>
              </div>}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:14, borderTop:'1px solid var(--border-light)' }}>
                <span className="text-secondary">Taxable Amount</span><span className="font-mono">₹{fmt(summary.taxable)}</span>
              </div>
              {!summary.isIGST ? (
                <>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
                    <span className="text-secondary">CGST</span><span className="font-mono">₹{fmt(summary.cgst)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
                    <span className="text-secondary">SGST</span><span className="font-mono">₹{fmt(summary.sgst)}</span>
                  </div>
                </>
              ) : (
                <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
                  <span className="text-secondary">IGST</span><span className="font-mono">₹{fmt(summary.igst)}</span>
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12 }}>
                <span className="text-secondary">Round Off</span><span className="font-mono">{roundOff}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 0', fontSize:18, fontWeight:700, borderTop:'2px solid var(--text)', marginTop:8 }}>
                <span>Total</span><span className="font-mono">₹{fmt(roundedTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3" style={{ marginTop: 24, paddingBottom: 40 }}>
        <button onClick={handleSubmit} className={`btn ${invoiceType === 'gst_invoice' ? 'btn-primary' : 'btn-accent'} btn-lg`} disabled={loading || items.length === 0}>
          {loading ? 'Creating...' : invoiceType === 'proforma' ? 'Create Proforma Invoice' : 'Create GST Invoice'}
        </button>
        <button onClick={() => navigate('/invoices')} className="btn btn-secondary btn-lg">Cancel</button>
      </div>
    </div>
  );
}
